import { ChangeEvent, Dispatch, SetStateAction } from "react"
import { EcleanCuration, qualifierOptions, unitOptions } from "../GenerateDSComponents/Curate"
import InfoTooltip from "../../../../components/Buttons/InfoTooltip"
import ConditionalSelectField from "../../../../components/Form/ConditionSelectField"
import SelectField from "../../../../components/Form/SelectFields"
import InputField from "../../../../components/Form/InputField"

export default function Binarize({
    showSelect,
    form,
    setForm,
    fieldOptions,
    menuPortalTargetId,
}: {
    showSelect: boolean,
    form: EcleanCuration,
    setForm: Dispatch<SetStateAction<EcleanCuration>>,
    fieldOptions: {
        value: string,
        label: string
    }[],
    menuPortalTargetId: string,
}) {

    const handleInputBinary = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const name = e.target.name;
        const value = e.target.value;

        if (form.eclean_std_binary) {
            setForm({
                ...form,
                eclean_std_binary: {
                    ...form.eclean_std_binary,
                    [name]: value,
                }
            })
        }
    }

    const handleSelectBinary = (e:any, name: string) => {
        const value = e.value;

        if (form.eclean_std_binary) {
            setForm({
                ...form,
                eclean_std_binary: {
                    ...form.eclean_std_binary,
                    [name]: value,
                }
            })
        }
    }

    const handleSelectValueOrQualifier = (e:any, name: string) => {
        const value = e.value;

        if (form.eclean_std_binary) {
            setForm({
                ...form,
                eclean_std_binary: {
                    ...form.eclean_std_binary,
                    [name]: value,
                },
                eclean_std_values: form.eclean_std_values ? {
                    ...form.eclean_std_values,
                    [name]: value,
                } : null,
                eclean_std_regression: form.eclean_std_regression ? {
                    ...form.eclean_std_regression,
                    [name]: value,
                } : null,
            })
        }
    }

    const handleSelectStdBinaryValues = (e: any, name: string) => {
        const value = e.value;

        if (form.eclean_std_binary) {
            setForm({
                ...form,
                eclean_std_binary: {
                    ...form.eclean_std_binary,
                    [name]: value,
                },
                eclean_std_binary_values: form.eclean_std_binary_values ? {
                    ...form.eclean_std_binary_values,
                    [name]: value,
                } : null,
            })
        }
    }

    const handleSelectUnits = (e:any, name: string) => {
        const value = e.value;

        if (form.eclean_std_binary && form.eclean_std_binary.binarization_threshold_unit_conversion) {
            setForm({
                ...form,
                eclean_std_binary: {
                    ...form.eclean_std_binary,
                    binarization_threshold_unit_conversion: {
                        ...form.eclean_std_binary.binarization_threshold_unit_conversion,
                        [name]: value,
                    }
                }
            })
        }
    }

    return (
        <>
        {form.eclean_std_binary !== null && (
            // Single-class & continuous
            // If smiles string was previously standardized, set as smiles column
            <div className="flex flex-col gap-6 justify-start items-start">
                <div className="flex justify-start items-center gap-2">
                    <p className="text-default font-semibold">{form.eclean_std_smiles !== null ? '3' : '2'}. Binarize Values</p>
                    <InfoTooltip 
                        btnWidth="14px"
                        title="Binarize Values"
                        content={
                            <div className="flex flex-col gap-3 text-placeholder text-[12px]">
                                <p className="max-w-[300px]">This pipeline binarizes values based in the given threshold and qualifier, combines binary values based on the binary agreement ratio, and discards binaries that do not agree above the given threshold.</p>
                            </div>
                        }
                    />
                </div>
                
                {form.eclean_std_binary.binary_value_column !== null && (
                    <div className={`w-full flex justify-between items-end gap-4`}>
                        <div className={`${form.eclean_std_smiles !== null ? 'w-full' : 'w-1/2'}`}>
                            <ConditionalSelectField label='Binary value column' showSelect={showSelect}>
                                <SelectField 
                                    label="Binary value column"
                                    id="binary_value_column"
                                    name="binary_value_column"
                                    value={form.eclean_std_binary.binary_value_column ? {
                                        value: form.eclean_std_binary.binary_value_column,
                                        label: form.eclean_std_binary.binary_value_column
                                    } : null}
                                    options={fieldOptions}
                                    isRequired={true}
                                    isCreatable={false}
                                    isMulti={false}
                                    handleChange={handleSelectStdBinaryValues}
                                    menuPortalTarget={menuPortalTargetId ? document.getElementById(menuPortalTargetId) : document.getElementById('curate-ds-popup')}
                                    />
                            </ConditionalSelectField>
                        </div>
                        {form.eclean_std_binary.smiles_column !== null && (
                            <SelectField 
                                label="Standardized SMILES Column"
                                id="smiles_column"
                                name="smiles_column"
                                value={{ 
                                    value: form.eclean_std_binary.smiles_column, 
                                    label: form.eclean_std_binary.smiles_column 
                                }}
                                options={fieldOptions}
                                isRequired={true}
                                isCreatable={false}
                                isMulti={false}
                                disabled
                                handleChange={() => {}}
                                menuPortalTarget={menuPortalTargetId ? document.getElementById(menuPortalTargetId) : document.getElementById('curate-ds-popup')}
                            />
                        )}
                    </div>
                )}

                {form.eclean_std_binary.binarization_threshold !== null && form.eclean_std_binary.binarization_qualifier !== null && form.eclean_std_binary.binarization_threshold_unit_conversion !== null && (
                    <div className="w-full flex justify-between items-end gap-4">
                        <InputField 
                            label="Binarization threshold"
                            id="binarization_threshold"
                            name="binarization_threshold"
                            value={form.eclean_std_binary.binarization_threshold}
                            isRequired={true}
                            handleChange={handleInputBinary}
                            type="text"
                        />
                        <ConditionalSelectField label='Binarization qualifier' showSelect={showSelect}>
                            <SelectField 
                                label="Binarization qualifier"
                                id="binarization_qualificer"
                                name="binarization_qualifier"
                                value={form.eclean_std_binary.binarization_qualifier ? {
                                    value: form.eclean_std_binary.binarization_qualifier,
                                    label: qualifierOptions.find(opt => opt.value === form.eclean_std_binary!.binarization_qualifier)!.label
                                } : null}
                                options={qualifierOptions}
                                isRequired={true}
                                isCreatable={false}
                                isMulti={false}
                                handleChange={handleSelectBinary}
                                menuPortalTarget={menuPortalTargetId ? document.getElementById(menuPortalTargetId) : document.getElementById('curate-ds-popup')}
                                />
                        </ConditionalSelectField>
                        <ConditionalSelectField label='Threshold Units' showSelect={showSelect}>
                            <SelectField 
                                label="Threshold Units"
                                id="binarization_units"
                                name="binarization_units"
                                value={form.eclean_std_binary.binarization_threshold_unit_conversion.binarization_units ? { 
                                    value: form.eclean_std_binary.binarization_threshold_unit_conversion.binarization_units, 
                                    label: form.eclean_std_binary.binarization_threshold_unit_conversion.binarization_units 
                                } : null}
                                options={unitOptions}
                                isRequired={true}
                                isCreatable={false}
                                isMulti={false}
                                handleChange={handleSelectUnits}
                                menuPortalTarget={menuPortalTargetId ? document.getElementById(menuPortalTargetId) : document.getElementById('curate-ds-popup')}
                                />
                        </ConditionalSelectField>
                    </div>
                )}

                {form.eclean_std_binary.value_column !== null && form.eclean_std_binary.qualifier_column !== null && (
                    <div className="w-full flex justify-between items-end gap-4">
                        <ConditionalSelectField label='Value Column' showSelect={showSelect}>
                            <SelectField 
                                label="Value Column"
                                id="value_column"
                                name="value_column"
                                value={form.eclean_std_binary.value_column ? { 
                                    value: form.eclean_std_binary.value_column, 
                                    label: form.eclean_std_binary.value_column 
                                } : null}
                                options={fieldOptions}
                                isRequired={true}
                                isCreatable={false}
                                isMulti={false}
                                handleChange={handleSelectValueOrQualifier}
                                menuPortalTarget={menuPortalTargetId ? document.getElementById(menuPortalTargetId) : document.getElementById('curate-ds-popup')}
                                />
                        </ConditionalSelectField>
                        <ConditionalSelectField label='Qualifier Column' showSelect={showSelect}>
                            <SelectField 
                                label="Qualifier Column"
                                id="qualifier_column"
                                name="qualifier_column"
                                value={form.eclean_std_binary.qualifier_column ? { 
                                    value: form.eclean_std_binary.qualifier_column, 
                                    label: form.eclean_std_binary.qualifier_column 
                                } : null}
                                options={fieldOptions}
                                isRequired={false}
                                isCreatable={false}
                                isMulti={false}
                                handleChange={handleSelectValueOrQualifier}
                                menuPortalTarget={menuPortalTargetId ? document.getElementById(menuPortalTargetId) : document.getElementById('curate-ds-popup')}
                                />
                        </ConditionalSelectField>
                    </div>
                )}

                {form.eclean_std_binary.smiles_column !== null && (
                <div className="w-full">
                    <SelectField 
                        label="Standardized SMILES Column"
                        id="smiles_column"
                        name="smiles_column"
                        value={{ 
                            value: form.eclean_std_binary.smiles_column, 
                            label: form.eclean_std_binary.smiles_column 
                        }}
                        options={fieldOptions}
                        isRequired={true}
                        isCreatable={false}
                        isMulti={false}
                        disabled
                        handleChange={() => {}}
                        menuPortalTarget={menuPortalTargetId ? document.getElementById(menuPortalTargetId) : document.getElementById('curate-ds-popup')}
                    />
                </div>
                )}
                
            </div>
        )}
        </>
    )
}